import { Component, OnInit, Output, EventEmitter, ViewChild, Input, OnChanges, AfterViewInit, Optional, SimpleChanges } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { SubmissionParams } from '../../_models/session';
import { UserService, ActionService, SessionService, AuthenticationService } from  '../../_services/index';
import { CrmUserComponent } from '../../shared/user/crm.user.component';
import { ClientDataHelper } from '../../_helpers/client-data-helper';
import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { ActionStatus } from '../../_models/actions';
import { ACTION_OVERDUE_DAYS } from '../../_constants/action-rules';
import { PublicSettingsService } from '../../_services/app-settings/public-settings.service';
import { take } from 'rxjs/operators';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FilterSpec, FilterState } from '../filter-ux/filter-state';
import { ScreenFilterUx } from '../filter-ux/screen-filter-ux';
import { DatePreset, isDatePreset, resolveDatePreset, toIsoDate } from '../filter-ux/date-presets';
import { DatePresetSelection } from '../filter-ux/date-presets.component';

@Component({
    selector: 'search',
    templateUrl: 'search.component.html',
    styleUrls: ['search.component.css'],
    standalone: false
})

export class SearchComponent implements OnInit, OnChanges, AfterViewInit {

  @Output() onSearched: EventEmitter<any> = new EventEmitter<any>();
  @Input() filteredData: FilteredData[] = [];
  /**
   * The host screen's per-screen filter switch, resolved by the host (the actions screen passes
   * `FeatureSwitchService.isEnabled(FeatureSwitchKeys.filtersActions)`). Stable contract — later filter
   * UX (remember filters, Clear all, date presets, URL state) builds on this input.
   *
   * - `false` (the default, and whenever the host has not resolved the switch): the pre-filter screen.
   *   No brand filter, no Assigned-to-me toggle, and the emitted search parameters carry neither
   *   `companyIds` nor `assignedToMe`.
   * - `true`: the Brand filter and the new filters show, and their values are sent.
   */
  @Input() filtersEnabled: boolean = false;
  /**
   * The host's search.autocomplete.actions switch. On, the Client, Tutor and Client Referrer boxes suggest the
   * distinct names in the actions this component has already loaded (no extra fetch). Off, they are plain boxes.
   */
  @Input() autocompleteEnabled: boolean = false;
  /** Suggestions for the Client, Tutor and Client Referrer boxes: distinct, non-empty names from the loaded actions. */
  public clientNames: string[] = [];
  public tutorNames: string[] = [];
  public referrerNames: string[] = [];
  @ViewChild(CrmUserComponent, { static: false }) crmUser;

  /** Selected brands; empty means every brand the user may see. */
  public companyIds: number[] = [];
  /** Assigned to me: actions on placements the signed-in user owns as consultant. */
  public assignedToMe: boolean = false;
  /** Open (the default, and today's list), done (completed) or all. */
  public status: ActionStatus = 'open';
  /** Overdue: open actions raised more than ACTION_OVERDUE_DAYS days ago. */
  public overdue: boolean = false;
  /** The served threshold (api/Settings/Public); ACTION_OVERDUE_DAYS until it arrives or if it fails. */
  public overdueDays: number = ACTION_OVERDUE_DAYS;
  /** The date preset behind the From/To dates, or null when they were set by hand. */
  public datePreset: DatePreset | null = null;

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
   * only: free text, names and emails stay out of the URL (browser history, telemetry) and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    task: 'string[]', brand: 'number[]', mine: 'boolean', from: 'string', to: 'string', preset: 'string'
  };
  /** Remembered filters and filter state in the URL; used only while iltersEnabled is true. */
  private filterUx: ScreenFilterUx;
  private viewReady: boolean = false;
  private filterUxRestored: boolean = false;
  private urlFiltersApplied: boolean = false;
  frmSearch: UntypedFormGroup;
  searchResultText: string = "";
  errorMsg: string = "";
  submissionParams: SubmissionParams = null;
  showSearchExtraColumns: Boolean = false;
  user: any = {};
  studentData: Array<string>;
  public filteredStudents = new Array<string>();
  currentStudent: string;
  studentValue: string[];
  taskData: Array<string>;
  public filteredTasks = new Array<string>();
  currentTask: string;
  taskValue: string[];
  initialData: any[] = [];
  searchData: string = "actionSearch";

  public studentTooltip: string;

  private staffFilter: boolean = false;
  
  constructor(
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private actionService: ActionService,
    private sessionService: SessionService,
    private clientDataHelper: ClientDataHelper, 
    private authService: AuthenticationService,
    public themeService: ThemeService,
    private featureHelperService: FeatureHelperService,
    @Optional() private router: Router = null,
    @Optional() private route: ActivatedRoute = null,
    @Optional() private publicSettings: PublicSettingsService = null
    ) {
    this.filterUx = new ScreenFilterUx(FeatureSwitchKeys.filtersActions, SearchComponent.filterSpec, this.router, this.route);
  }

  ngOnInit() {
    this.publicSettings?.actionsOverdueDays().pipe(take(1)).subscribe(days => this.overdueDays = days);

    let featureList = new Array<string>();
    featureList.push("StaffFilter");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    this.staffFilter = currentUserFeatures.filter(f => f.feature === "StaffFilter")[0].enabled;

    if (!this.staffFilter) { 
      this.showSearchExtraColumns = true;
    }
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    this.frmSearch = this.formBuilder.group({
      startDate: [formatDate(oneWeekAgo, 'yyyy-MM-dd', 'en-GB')],
      endDate: [formatDate(today, 'yyyy-MM-dd', 'en-GB')],
      tutor: null,
      clientReferrer: null,
      client: null,
      student: null,
      task : null
    });
    this.loadTaskOptions();
    this.getFilterData();
    setTimeout(() => {
      this.studentTooltip = "Please select a learner";
    }, 100);
  }

  ngAfterViewInit() {
    let clientData = this.clientDataHelper.getData(this.searchData);
    if (clientData !== null && clientData !== undefined) {
      if (this.crmUser !== null && this.crmUser !== undefined) {
        this.crmUser.UserData.subscribe({
          next: (n) =>
            this.setClientData(true)
        });
      }
      else {
        this.setClientData(false);
      }
    }
    else {
      // No saved filter: apply the prepopulated date range so the first load is filtered, rather than
      // leaving the grid unfiltered until Search is clicked. Deliberately not stored - a persisted
      // default would come back with stale dates on the next visit.
      this.emitSearch();
    }

    this.viewReady = true;
    this.restoreFilterUx();
  }

  setClientData(setUser) {
    let clientData = this.clientDataHelper.getData(this.searchData);
    // A shared link's filters, once applied, win over this tab's last search.
    if (clientData !== null && !this.urlFiltersApplied) {
      this.frmSearch.get("tutor").setValue(clientData.tutor);
      this.frmSearch.get("startDate").setValue(clientData.startDate);
      this.frmSearch.get("endDate").setValue(clientData.endDate);
      this.frmSearch.get("student").setValue(clientData.student);
      this.frmSearch.get("clientReferrer").setValue(clientData.clientReferrer);
      this.frmSearch.get("task").setValue(clientData.task);
      this.frmSearch.get("client").setValue(clientData.client);
      this.currentStudent = clientData.student;
      this.studentValue = clientData.student.split('|');
      this.currentTask = clientData.task;
      this.taskValue = clientData.task.split('|');
      if (this.filtersEnabled) {
        this.companyIds = clientData.companyIds ?? [];
        this.assignedToMe = clientData.assignedToMe === true;
        this.status = clientData.status === 'done' || clientData.status === 'all' ? clientData.status : 'open';
        this.overdue = clientData.overdue === true;
      }
      if (setUser) {
        this.crmUser.setUsers(clientData.user)
      }
      this.search();
    }
  }

  getFilterData() {
    this.populateParams();
    this.actionService.getActions(this.staffFilter).subscribe((data: any) => {
      this.initialData = data;
      if (this.initialData !== null && this.initialData !== undefined && this.initialData.length > 0) {
        this.getPlacements();
      }
    });
  }

  studentChanged(data: any) {
    if (data !== null && data !== undefined) {
      this.currentStudent = data.join('|');
    }
  }
  taskChanged(data: any) {
    if (data !== null && data !== undefined) {
      this.currentTask = data.join('|');
    }
  }
  brandsChanged(companyIds: number[]) {
    this.companyIds = companyIds ?? [];
  }

  ngOnChanges(changes?: SimpleChanges) {
    // The host resolves the switch asynchronously, so it may turn on after the first load.
    if (changes?.filtersEnabled && this.filtersEnabled) {
      this.restoreFilterUx();
    }
  }

  /**
   * With the switch on, a shared link's filters win. Otherwise this tab's last search (already restored
   * by ngAfterViewInit) stands, and failing that, this user's remembered filters apply. Runs once.
   */
  private restoreFilterUx() {
    if (this.filterUxRestored || !this.filtersEnabled || !this.viewReady) {
      return;
    }
    this.filterUxRestored = true;
    const hasTabSearch = this.clientDataHelper.getData(this.searchData) != null;
    const fromUrl = this.filterUx.urlState();
    this.urlFiltersApplied = fromUrl != null;
    const state = fromUrl ?? (hasTabSearch ? null : this.filterUx.rememberedState());
    if (state) {
      this.applyFilterState(state);
      this.search();
    }
  }

  public onDatePreset(selection: DatePresetSelection) {
    this.datePreset = selection.preset;
    this.frmSearch.patchValue({ startDate: selection.from, endDate: selection.to });
  }

  /** Clear all: back to the default week, forgotten, and out of the URL. */
  public clearAll() {
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    this.frmSearch.reset({
      startDate: formatDate(oneWeekAgo, 'yyyy-MM-dd', 'en-GB'),
      endDate: formatDate(today, 'yyyy-MM-dd', 'en-GB')
    });
    this.currentStudent = null;
    this.studentValue = null;
    this.currentTask = null;
    this.taskValue = null;
    this.companyIds = [];
    this.assignedToMe = false;
    this.datePreset = null;
    this.crmUser?.reset();
    this.clientDataHelper.storeData(null, this.searchData);
    this.filterUx.clear();
    this.emitSearch();
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    // Read the controls: populateParams() overwrites frmSearch.value's dates with Date objects.
    const value = this.frmSearch.getRawValue();
    return {
      task: this.currentTask ? this.currentTask.split('|') : null,
      brand: this.companyIds,
      mine: this.assignedToMe || null,
      preset: this.datePreset,
      from: this.datePreset ? null : SearchComponent.isoDate(value.startDate),
      to: this.datePreset ? null : SearchComponent.isoDate(value.endDate)
    };
  }

  private applyFilterState(state: FilterState) {
    this.datePreset = isDatePreset(state.preset) ? state.preset : null;
    const range = this.datePreset ? resolveDatePreset(this.datePreset) : null;
    this.frmSearch.patchValue({
      startDate: range ? range.from : (state.from ?? this.frmSearch.get('startDate').value),
      endDate: range ? range.to : (state.to ?? this.frmSearch.get('endDate').value)
    });
    const tasks = (state.task as string[]) ?? [];
    this.taskValue = tasks;
    this.currentTask = tasks.length ? tasks.join('|') : null;
    this.companyIds = (state.brand as number[]) ?? [];
    this.assignedToMe = state.mine === true;
  }

  /** The form holds a yyyy-MM-dd string until the user edits the date, then a Date. */
  private static isoDate(value: any): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : toIsoDate(date);
  }

  /**
   * Loads the Task filter's options from the action type reference data.
   *
   * This list used to be derived from whatever rows the current search returned, which meant a user
   * could only filter by tasks already visible, and a search that found nothing left the dropdown empty
   * with no way to narrow down (37488, 37294). The vocabulary does not depend on the results, so it is
   * fetched once and the selection is reconciled against it.
   *
   * A failed load leaves the dropdown empty rather than breaking the screen — no worse than the
   * behaviour it replaces, and the rest of the search still works.
   */
  private loadTaskOptions() {
    this.actionService.getAllActionTypes().subscribe({
      next: (types: any[]) => this.setTaskOptions(types ?? []),
      error: () => this.setTaskOptions([]),
    });
  }

  private setTaskOptions(types: any[]) {
    const names: string[] = [];
    types
      .slice()
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .forEach(t => {
        // Several action types can share a short description; the filter matches on that string, so the
        // options are the distinct descriptions rather than one entry per type.
        const name = t.shortDescription;
        if (name && names.indexOf(name) === -1) {
          names.push(name);
        }
      });

    this.taskData = names;
    this.filteredTasks = names;
    this.reconcileSelectedTasks();
  }

  /** Drops any selected task that is not a real action type (e.g. a stale saved search). */
  private reconcileSelectedTasks() {
    if (this.currentTask === null || this.currentTask === undefined) {
      return;
    }
    const selected = this.currentTask.split('|').filter(v => this.taskData.indexOf(v) !== -1);
    this.taskValue = selected;
    this.currentTask = selected.join('|');
  }

  getStudentData(data) {
    var tmpStudentData = [];
    data.forEach((el, i) => {
      var st = tmpStudentData.filter(x => x === el.studentName);
      if (st.length === 0 && el.studentName !== null && el.studentName != "") {
        tmpStudentData.push(el.studentName);
      }
    })
    this.studentData = tmpStudentData;
    this.filteredStudents = tmpStudentData;
    this.clientNames = SearchComponent.distinctNames(data, 'clientName');
    this.tutorNames = SearchComponent.distinctNames(data, 'tutorName');
    this.referrerNames = SearchComponent.distinctNames(data, 'referrerName');
    if (this.currentStudent !== null) {
      var tmpCurStudents = []; 
      var stValue = this.currentStudent.split('|');
      tmpStudentData.forEach((el, i) => stValue.forEach((v) => {
        if (el === v && tmpCurStudents.indexOf(v) == -1) {
          tmpCurStudents.push(v);
        }
      }))

      this.studentValue = tmpCurStudents;
      this.currentStudent = tmpCurStudents.join('|');
    }
  }

  /** The distinct, non-empty, trimmed values of one field, in first-seen order. */
  private static distinctNames(rows: any[], field: string): string[] {
    const names = new Set<string>();
    (rows ?? []).forEach(row => {
      const name = (row?.[field] ?? '').toString().trim();
      if (name) {
        names.add(name);
      }
    });
    return Array.from(names);
  }

  populateParams() {
    this.submissionParams = this.frmSearch.value;
    if (this.crmUser) {
      this.submissionParams.user = this.crmUser.selectedAllUsers ? null : this.crmUser.frmCrmUser.value.user;
    }
    this.submissionParams.student = this.currentStudent;
    this.submissionParams.task = this.currentTask;
    // New parameters only when the host's switch is on; off, the keys are absent altogether.
    if (this.filtersEnabled) {
      this.submissionParams.companyIds = this.companyIds;
      this.submissionParams.assignedToMe = this.assignedToMe;
      this.submissionParams.status = this.status;
      this.submissionParams.overdue = this.overdue;
    } else {
      delete this.submissionParams.companyIds;
      delete this.submissionParams.assignedToMe;
      delete this.submissionParams.status;
      delete this.submissionParams.overdue;
    }
    var dateString = new Date(this.frmSearch.get("startDate").value).toUTCString();
    var startDate = dateString.split(' ').slice(0, 4).join(' ');
    var endDate = this.frmSearch.get("endDate").value;
    this.submissionParams.startDate = new Date(this.setDate(startDate));
    this.submissionParams.endDate = new Date(this.setDate(endDate));
  }

  /** Applies the current filter to the host grid without persisting it. */
  private emitSearch() {
    this.resetValues();
    this.populateParams();
    this.onSearched.emit(this.frmSearch.value);
  }

  search() {
    this.emitSearch();

    let param = this.frmSearch.value;
    param.student = this.currentStudent;
    param.task = this.currentTask;
    this.clientDataHelper.storeData(param, this.searchData);
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }
  }

  resetFilter() {
    this.frmSearch.setValue({
      student: null,
      task: null,
      clientReferrer: null,
      tutor: null,
      startDate: null,
      client: null,
      endDate: new Date(this.setDate(new Date())),
    });

    this.submissionParams = this.frmSearch.value;
    this.submissionParams.student = null;
    this.submissionParams.task = null;
    this.submissionParams.clientReferrer = null;
    this.submissionParams.tutor = null;
    this.submissionParams.endDate = new Date(this.setDate(new Date()));
    var usr = this.crmUser.reset();
    this.submissionParams.user = usr;
    this.getStudentData(this.initialData);
    this.studentValue = null;
    this.taskValue = null;
    this.onSearched.emit(this.frmSearch.value);
  }

  validateForm(): boolean {
    var ret = false;
    Object.keys(this.frmSearch.controls).forEach(key => {
      if (this.frmSearch.controls[key].value !== null && this.frmSearch.controls[key].value !== undefined) {
        ret = true;
      }
    });
    return ret;
  }

  resetValues() {
    this.searchResultText = '';
    this.errorMsg = '';
  }

  setDate(date) {
    var d = new Date(date);
    return d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  }

  public getPlacements() {
    var data = this.initialData;
    let result = data.map(a => a.placementId);
    this.populateParams();
    var crmUser = [];
    var cUser = this.actionService.currUser;
    if (cUser.userRelationshipId === 9 || cUser.userRelationshipId === 12) {
      crmUser.push({ email: cUser.currentUser.email, userRelationshipId: cUser.userRelationshipId });
    }
    
    var params: any = {};
    params.placementIds = result;
    params.user = crmUser;

    this.sessionService.getPlacements(params).subscribe((result: any) => {
      var tmpData = [];
      if (result.length > 0) {
        for (var i = 0; i < data.length; i++) {
          let tData = result.filter(x => x.placementID === data[i].placementId)[0];
          if (tData) {
            data[i].clientName = tData.clientName;
            data[i].tutorName = tData.tutorName;
            data[i].tutorId = tData.tutorId;
            data[i].referrerName = tData.referrerName;
            data[i].studentName = tData.studentName;
            tmpData.push(data[i]);
          }
        }
      }
      this.getStudentData(tmpData);

      //let clientData = this.clientDataHelper.getData(this.searchData);
      //if (clientData != null) {
      //  //this.setClientData();
      //  //this.search();
      //}
    })
  }

  public onUserDataChanged($event) {
    this.frmSearch.get("startDate").setValue(null);
    this.populateParams();
    this.actionService.getActionsSearch(this.submissionParams, this.staffFilter).subscribe((data: any) => {
      this.initialData = data;
      var params: any = {};
      params.placementIds = this.initialData.map(x => x.placementId);
      params.user = $event.selectedAllUsers ? [] : $event.user;
      if ($event.selectedAllUsers) {
        params.searchByUser = false;
      }
      this.sessionService.getPlacements(params).subscribe((result: any) => {
        var tmpData = [];
        if (result.length > 0) {
          for (var i = 0; i < data.length; i++) {
            let tData = result.filter(x => x.placementID === data[i].placementId)[0];
            if (tData) {
              data[i].clientName = tData.clientName;
              data[i].tutorName = tData.tutorName;
              data[i].tutorId = tData.tutorId;
              data[i].referrerName = tData.referrerName;
              data[i].studentName = tData.studentName;
              tmpData.push(data[i]);
            }
          }
        }
        this.getStudentData(tmpData);
      })
    });
  }

  public filterStudents(result: Array<any>): void {
    this.filteredStudents = new Array<string>();
    if (this.currentStudent !== null && this.currentStudent !== undefined) {
      let studentHolder = this.currentStudent.split('|');
      this.filteredStudents.push(...studentHolder);
      result = result.filter(r => !studentHolder.includes(r));
    }
    this.filteredStudents.push(...result);
  }
  public filterTasks(result: any): void {
    this.filteredTasks = new Array<string>();
    if (this.currentTask !== null && this.currentTask !== undefined) {
      let taskHolder = this.currentTask.split('|');
      this.filteredTasks.push(...taskHolder);
      result = result.filter(r => !taskHolder.includes(r));
    }
    this.filteredTasks.push(...result);
  }
}

export class FilteredData {
  studentName: string;
  actionType: any = {};
  createdDate: Date;
}
